import {
	Autocomplete,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	TextField,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import type {
	ProductNode,
	StraightenerRecipeVersion,
} from "../models/StraightenerRecipe";

interface ProductOption {
	label: string;
	node: ProductNode;
}

interface RecipeOption {
	label: string;
	recipe: StraightenerRecipeVersion;
}

interface Props {
	open: boolean;
	// All product nodes with their recipe versions.
	nodes: ProductNode[];
	// Pre-selected product (the one the new recipe belongs to).
	defaultProductId: number | null;
	// Recipe to open on. When it is not a valid source, the product's default
	// recipe, then its most recent one.
	defaultRecipeId?: number | null;
	isCopying: boolean;
	onCopy: (recipe: StraightenerRecipeVersion) => void;
	onCancel: () => void;
}

// Copy the values of any active recipe into a new one. First pick the source
// product (defaults to the current product), then one of its active recipes
// (defaults to the given recipe, else the default one, else the most recent).
// Only products that have at least one active recipe are listed.
export const CopyFromRecipeModal = ({
	open,
	nodes,
	defaultProductId,
	defaultRecipeId = null,
	isCopying,
	onCopy,
	onCancel,
}: Props) => {
	const productOptions = useMemo<ProductOption[]>(
		() =>
			nodes
				.filter((n) => n.versions.some((v) => v.IsActive))
				.map((n) => ({
					label: `(${n.productTypeName}) ${n.productName}`,
					node: n,
				})),
		[nodes],
	);

	const [product, setProduct] = useState<ProductOption | null>(null);
	const [recipe, setRecipe] = useState<RecipeOption | null>(null);

	const optionsFor = useCallback(
		(node: ProductNode | undefined): RecipeOption[] =>
			(node?.versions ?? [])
				.filter((v) => v.IsActive)
				.map((v) => ({
					label: `${v.Name ? `${v.Name} · ` : ""}v${v.Version}${
						v.Crew ? ` · Crew ${v.Crew}` : ""
					}${v.IsCurrent ? " · default" : ""}`,
					recipe: v,
				})),
		[],
	);

	// On open, default to the current product (when it has active recipes) and
	// preselect a recipe so the common case is a single click. The versions come
	// sorted default first, then most recently modified, so the first option is
	// the fallback in the right order.
	useEffect(() => {
		if (!open) return;
		const def =
			productOptions.find((o) => o.node.productSizeId === defaultProductId) ??
			null;
		setProduct(def);
		const opts = optionsFor(def?.node);
		setRecipe(
			opts.find((o) => o.recipe.Id === defaultRecipeId) ?? opts[0] ?? null,
		);
	}, [open, defaultProductId, defaultRecipeId, productOptions, optionsFor]);

	const recipeOptions = useMemo<RecipeOption[]>(
		() => optionsFor(product?.node),
		[product, optionsFor],
	);

	return (
		<Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
			<DialogTitle sx={{ fontWeight: 600 }}>COPY FROM OTHER RECIPE</DialogTitle>
			<DialogContent
				sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}
			>
				<Autocomplete
					options={productOptions}
					value={product}
					onChange={(_, v) => {
						setProduct(v);
						setRecipe(null);
					}}
					getOptionLabel={(o) => o.label}
					isOptionEqualToValue={(a, b) =>
						a.node.productSizeId === b.node.productSizeId
					}
					noOptionsText="No products with active recipes"
					renderInput={(params) => (
						<TextField {...params} label="Product" size="small" />
					)}
				/>
				<Autocomplete
					options={recipeOptions}
					value={recipe}
					onChange={(_, v) => setRecipe(v)}
					getOptionLabel={(o) => o.label}
					isOptionEqualToValue={(a, b) => a.recipe.Id === b.recipe.Id}
					disabled={!product}
					noOptionsText="This product has no active recipes"
					renderInput={(params) => (
						<TextField {...params} label="Recipe" size="small" />
					)}
				/>
			</DialogContent>
			<DialogActions sx={{ px: 3, pb: 2 }}>
				<Button
					variant="contained"
					onClick={onCancel}
					sx={{
						backgroundColor: "#e0e0e0",
						color: "rgba(0, 0, 0, 0.87)",
						"&:hover": { backgroundColor: "#d5d5d5" },
					}}
				>
					CANCEL
				</Button>
				<Button
					color="primary"
					variant="contained"
					disabled={!recipe || isCopying}
					onClick={() => recipe && onCopy(recipe.recipe)}
				>
					{isCopying ? "COPYING..." : "COPY VALUES"}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
